/**
 * SummaryDrawerContext — aides NON-composants du volet de synthèse (contexte,
 * type de valeur, lecture synchrone du seuil desktop, hook d'accès), sorties
 * dans ce module frère : Fast Refresh n'opère que si un fichier n'exporte QUE
 * des composants.
 */

import { createContext, useContext } from 'react'

import type { PinnedSummary } from '@/components/summary/types'

/** Seuil « desktop » du volet — aligné sur le Help Drawer ancré (lg = 1024px). */
export const SUMMARY_DESKTOP_QUERY = '(min-width: 1024px)'

/** Lecture SYNCHRONE du seuil desktop (pour les prédicats `visible` de RowAction). */
export function isSummaryDesktop(): boolean {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
    return window.matchMedia(SUMMARY_DESKTOP_QUERY).matches
}

export interface SummaryDrawerContextValue {
    /** Fiche épinglée (null = volet fermé). */
    pinned: PinnedSummary | null
    /** Épingle une fiche (remplace la précédente — mono-slot). No-op sous 1024px. */
    pin: (target: PinnedSummary) => void
    /** Ferme le volet (= dé-épingle). */
    unpin: () => void
    /** Écran ≥ 1024px (réactif). */
    isDesktop: boolean
    /** Volet effectivement affichable (épinglé ET desktop). */
    visible: boolean
}

const noop = () => {}

export const SummaryDrawerContext = createContext<SummaryDrawerContextValue | null>(null)

/**
 * Accès au volet de synthèse. Sûr hors provider (no-op + desktop synchrone) :
 * les hooks de listing (`useXxxActions`) l'appellent, et certains montages de
 * test / storybook n'ont pas le provider.
 */
export function useSummaryDrawer(): SummaryDrawerContextValue {
    const ctx = useContext(SummaryDrawerContext)
    if (ctx) return ctx
    return {
        pinned: null,
        pin: noop,
        unpin: noop,
        isDesktop: isSummaryDesktop(),
        visible: false,
    }
}
